import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, CircularProgress, Alert, TextField, Tooltip, Menu, MenuItem,
  ListItemIcon, ListItemText, ListSubheader, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  InputAdornment, Snackbar, LinearProgress
} from '@mui/material';
import {
  AutoAwesome, ChevronLeft, ChevronRight, PushPin, PushPinOutlined, Autorenew, Edit, MoreVert, Check,
  DragIndicator, Add, Search, DeleteOutline, EventBusy, EventAvailable, CloudDone, CloudSync, CloudOff, Send,
  TaskAlt, Undo, LockOpen
} from '@mui/icons-material';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors,
  DragEndEvent, DragStartEvent
} from '@dnd-kit/core';
import api from '../api';
import VersandDialog, { Versand, versandText } from './VersandDialog';
import { useKonto } from '../konto';

export interface Slot {
  gericht_id: number | null;
  name: string;
  fixiert?: boolean;
  angepasst?: boolean;
  vorgabe?: string;
}
// Tagesfreigabe: in Arbeit → fertig (gesperrt) → versendet; „Ändern“ öffnet einen versendeten Tag wieder
interface Freigabe {
  status: 'arbeit' | 'fertig' | 'versendet';
  fertig?: { am: string; name?: string };
  versendet?: { am: string; name?: string };
  geaendert?: boolean;
}
interface Tag {
  datum: string;
  aktiv: boolean;
  slots: Record<string, Slot>;
  vegNoetig?: Record<string, boolean>;
  freigabe?: Freigabe;
  nichtFreigegeben?: boolean;   // nur für Rolle Standard: Tag noch nicht versendet
}
const statusVon = (t: Tag) => t.freigabe?.status || 'arbeit';
const tagGesperrt = (t: Tag) => statusVon(t) !== 'arbeit';
const datumZeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
interface Gang { gang: string; label: string; nurWennNoetig: string | null }
interface Plan {
  jahr: number;
  kw: number;
  vorgaben: string[];
  tage: Tag[];
  gaenge: Gang[];
  hinweise: Record<string, string[]>;
  gespeichert: string | null;
  offeneVorgaben?: string[];
  versand?: Versand | null;
}
interface Alternative {
  gericht: { id: number; name: string };
  gruende: string[];
  hinweise: string[];
}

const TAG_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
// Für "Zuordnung ändern" (Ablage des Gerichts in der Datenbank)
const ZUORDNUNGEN = [
  { gang: 'vorspeise', label: 'Vorspeise' }, { gang: 'suppe', label: 'Suppe' },
  { gang: 'fleisch', label: 'Fleisch' }, { gang: 'fisch', label: 'Fisch' },
  { gang: 'alternative', label: 'Alternative' }, { gang: 'vegetarisch', label: 'Vegetarisch' },
  { gang: 'dessert', label: 'Dessert' },
];
const KATEGORIE_FUER: Record<string, string> = {
  vorspeise: 'Vorspeise', vegVorspeise: 'Vorspeise', suppe: 'Suppe', vegSuppe: 'Suppe',
  fleisch: 'Hauptgang', fisch: 'Hauptgang', alternative: 'Hauptgang', vegetarisch: 'Hauptgang', dessert: 'Dessert',
};

// ISO-Kalenderwoche eines Datums
export function kwVon(d: Date) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3);
  const jahr = t.getUTCFullYear();
  const ersterDo = new Date(Date.UTC(jahr, 0, 4));
  ersterDo.setUTCDate(ersterDo.getUTCDate() - ((ersterDo.getUTCDay() + 6) % 7) + 3);
  return { jahr, kw: 1 + Math.round((t.getTime() - ersterDo.getTime()) / (7 * 864e5)) };
}
const datumKurz = (d: string) => `${Number(d.slice(8, 10))}.${Number(d.slice(5, 7))}.`;
function zeitraum(tage: Tag[]) {
  if (!tage.length) return '';
  const a = tage[0].datum, b = tage[6].datum;
  const monat = (d: string) => MONATE[Number(d.slice(5, 7)) - 1];
  return a.slice(5, 7) === b.slice(5, 7)
    ? `${Number(a.slice(8))}.–${Number(b.slice(8))}. ${monat(b)} ${b.slice(0, 4)}`
    : `${Number(a.slice(8))}. ${monat(a)} – ${Number(b.slice(8))}. ${monat(b)} ${b.slice(0, 4)}`;
}
const zelleId = (tag: number, gang: string) => `${tag}|${gang}`;

// --- Gericht-Karte (ziehbar) ---
function GerichtKarte({ tag, gang, slot, hinweise, onFixieren, onAlternativen, onBearbeiten, onMenue, ziehbar, overlay }: {
  tag: number; gang: string; slot: Slot; hinweise: string[]; ziehbar: boolean; overlay?: boolean;
  onFixieren?: () => void; onAlternativen?: () => void; onBearbeiten?: () => void; onMenue?: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: zelleId(tag, gang), data: { tag, gang }, disabled: !ziehbar || overlay });
  return (
    <Box
      ref={overlay ? undefined : setNodeRef}
      sx={{
        bgcolor: 'background.paper', border: '1px solid', borderColor: slot.fixiert ? 'primary.main' : slot.vorgabe ? 'success.main' : 'divider',
        boxShadow: slot.fixiert ? 'inset 3px 0 0 #1976d2' : slot.vorgabe ? 'inset 3px 0 0 #2e7d32' : overlay ? 4 : 'none',
        borderRadius: 1.5, p: 1, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5,
        opacity: isDragging && !overlay ? 0.35 : 1, minHeight: 84,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        {ziehbar && (
          <Box {...listeners} {...attributes} aria-label="Verschieben" title="Ziehen, um mit einem anderen Tag zu tauschen"
            sx={{ cursor: 'grab', color: 'text.disabled', touchAction: 'none', mt: '1px', display: 'flex' }}>
            <DragIndicator sx={{ fontSize: 18 }} />
          </Box>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1, wordBreak: 'break-word' }}>{slot.name}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {slot.vorgabe && <Chip size="small" color="success" variant="outlined" label="Vorgabe" sx={{ height: 18, fontSize: '0.68rem' }} />}
        {slot.angepasst && <Chip size="small" variant="outlined" label="angepasst" sx={{ height: 18, fontSize: '0.68rem' }} />}
      </Box>
      {hinweise.map(h => (
        <Typography key={h} variant="caption" sx={{ bgcolor: '#fff4dc', color: '#7a4f00', borderRadius: 0.5, px: 0.5, lineHeight: 1.4 }}>
          Hinweis: {h}
        </Typography>
      ))}
      {!overlay && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', mx: -0.5, mb: -0.5 }}>
          {onFixieren && (
            <Tooltip title={slot.fixiert ? 'Fixierung lösen' : 'Fixieren – bleibt bei „Woche vorschlagen“'}>
              <IconButton size="small" onClick={onFixieren} aria-label={slot.fixiert ? 'Fixierung lösen' : 'Fixieren'} color={slot.fixiert ? 'primary' : 'default'}>
                {slot.fixiert ? <PushPin sx={{ fontSize: 17 }} /> : <PushPinOutlined sx={{ fontSize: 17 }} />}
              </IconButton>
            </Tooltip>
          )}
          {onAlternativen && (
            <Tooltip title="Alternativen anzeigen">
              <IconButton size="small" onClick={onAlternativen} aria-label="Alternativen"><Autorenew sx={{ fontSize: 17 }} /></IconButton>
            </Tooltip>
          )}
          {onBearbeiten && (
            <Tooltip title="Für diesen Tag anpassen">
              <IconButton size="small" onClick={onBearbeiten} aria-label="Für diesen Tag anpassen"><Edit sx={{ fontSize: 17 }} /></IconButton>
            </Tooltip>
          )}
          {onMenue && (
            <IconButton size="small" onClick={onMenue} aria-label="Weitere Aktionen"><MoreVert sx={{ fontSize: 17 }} /></IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}

function Zelle({ tag, gang, aktivGang, gesperrt, children }: { tag: number; gang: string; aktivGang: string | null; gesperrt?: boolean; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: zelleId(tag, gang), data: { tag, gang }, disabled: gesperrt || (aktivGang !== null && aktivGang !== gang) });
  const ziel = aktivGang === gang && !gesperrt;
  return (
    <Box ref={setNodeRef} sx={{
      minWidth: 0, borderRadius: 1.5, outline: ziel ? '2px dashed' : 'none', outlineColor: isOver ? 'primary.main' : 'divider',
      bgcolor: isOver ? 'action.hover' : 'transparent', transition: 'background-color 120ms',
    }}>
      {children}
    </Box>
  );
}

export default function Wochenplan({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }) {
  const heute = kwVon(new Date());
  const [woche, setWoche] = useState<{ jahr: number; kw: number }>(() => {
    // Standard: nächste Woche (geplant wird im Voraus)
    const d = new Date(); d.setDate(d.getDate() + 7);
    return kwVon(d);
  });
  const [plan, setPlan] = useState<Plan | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [status, setStatus] = useState<'gespeichert' | 'speichert' | 'fehler'>('gespeichert');
  const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);
  const [vorschlagFragen, setVorschlagFragen] = useState(false);
  const [vorgabeText, setVorgabeText] = useState('');
  const [meldung, setMeldung] = useState('');
  const [menue, setMenue] = useState<{ anker: HTMLElement; tag: number; gang: string } | null>(null);
  const [auswahl, setAuswahl] = useState<{ tag: number; gang: string } | null>(null);
  const [bearbeiten, setBearbeiten] = useState<{ tag: number; gang: string; text: string } | null>(null);
  const [ziehen, setZiehen] = useState<{ tag: number; gang: string } | null>(null);
  const [versandOffen, setVersandOffen] = useState(false);
  const { darfBearbeiten, istAdmin } = useKonto();
  const nurLesen = !darfBearbeiten;
  const speicherKette = useRef<Promise<void>>(Promise.resolve());
  const aktiv = useRef(true);
  const ladeAnfrage = useRef<AbortController | null>(null);
  const version = useRef(0);
  const gesperrt = laden || vorschlagLaeuft;
  const navigationGesperrt = gesperrt || status !== 'gespeichert';
  useEffect(() => {
    onBusyChange?.(navigationGesperrt);
    return () => onBusyChange?.(false);
  }, [navigationGesperrt, onBusyChange]);
  useEffect(() => {
    const warnen = (event: BeforeUnloadEvent) => {
      if (status !== 'gespeichert' || vorschlagLaeuft) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [status, vorschlagLaeuft]);
  useEffect(() => {
    aktiv.current = true;
    return () => { aktiv.current = false; ladeAnfrage.current?.abort(); };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const ladeWoche = useCallback(() => {
    ladeAnfrage.current?.abort();
    const controller = new AbortController();
    ladeAnfrage.current = controller;
    setLaden(true);
    setFehler('');
    setPlan(null);
    api.get(`/wochenplan/woche/${woche.jahr}/${woche.kw}`, { signal: controller.signal })
      .then(res => { if (!controller.signal.aborted) { setPlan(res.data); setStatus('gespeichert'); } })
      .catch(() => { if (!controller.signal.aborted) setFehler('Wochenplan konnte nicht geladen werden.'); })
      .finally(() => { if (!controller.signal.aborted) setLaden(false); });
  }, [woche]);
  useEffect(() => { ladeWoche(); }, [ladeWoche]);

  const nurDaten = (p: Plan) => ({ vorgaben: p.vorgaben, tage: p.tage.map(({ datum, aktiv, slots }) => ({ datum, aktiv, slots })) });

  // Schreibanfragen nacheinander senden: ältere Daten dürfen neuere nicht überschreiben.
  const aendern = (neu: Plan) => {
    setPlan(neu);
    setStatus('speichert');
    const meine = ++version.current;
    speicherKette.current = speicherKette.current.then(async () => {
      try {
        const res = await api.put(`/wochenplan/woche/${neu.jahr}/${neu.kw}`, nurDaten(neu));
        if (!aktiv.current || meine !== version.current) return;
        setPlan(p => (p ? { ...p, hinweise: res.data.hinweise, gespeichert: res.data.gespeichert, tage: res.data.tage } : p));
        setStatus('gespeichert');
      } catch {
        if (aktiv.current && meine === version.current) setStatus('fehler');
      }
    });
  };

  const slotSetzen = (tag: number, gang: string, slot: Slot | null) => {
    if (!plan) return;
    const tage = plan.tage.map((t, i) => {
      if (i !== tag) return t;
      const slots = { ...t.slots };
      if (slot) slots[gang] = slot; else delete slots[gang];
      return { ...t, slots };
    });
    aendern({ ...plan, tage });
  };

  const wocheVorschlagen = async () => {
    if (!plan || navigationGesperrt) return;
    setVorschlagFragen(false);
    setVorschlagLaeuft(true);
    version.current++;
    setFehler('');
    try {
      const res = await api.post(`/wochenplan/woche/${plan.jahr}/${plan.kw}/vorschlag`, nurDaten(plan));
      setPlan({ ...res.data, versand: plan.versand });
      setStatus('gespeichert');
      if (!res.data.offeneVorgaben?.length) setMeldung('Woche vorgeschlagen – fixierte Gerichte sind geblieben.');
    } catch {
      setFehler('Vorschlag fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setVorschlagLaeuft(false);
    }
  };
  const vorschlagStarten = () => {
    const ersetzt = plan?.tage.some(t => !tagGesperrt(t) && Object.entries(t.slots).some(([g, s]) => g !== 'vestibuel' && !s.fixiert));
    if (ersetzt) setVorschlagFragen(true); else wocheVorschlagen();
  };

  // Tag fertig markieren / zurücknehmen / versendeten Tag wieder öffnen (Server prüft und speichert)
  const tagFreigabe = async (tag: number, aktion: 'fertig' | 'zuruecknehmen' | 'aendern') => {
    if (!plan || navigationGesperrt) return;
    version.current++;
    setFehler('');
    try {
      const res = aktion === 'aendern'
        ? await api.post(`/wochenplan/woche/${plan.jahr}/${plan.kw}/tag/${tag}/aendern`, {})
        : await api.post(`/wochenplan/woche/${plan.jahr}/${plan.kw}/tag/${tag}/fertig`, { fertig: aktion === 'fertig' });
      setPlan({ ...res.data, versand: res.data.versand ?? plan.versand });
      setStatus('gespeichert');
    } catch (e: any) {
      setFehler(e?.response?.data?.fehler || 'Der Tag konnte nicht geändert werden.');
    }
  };

  const vorgabeHinzufuegen = () => {
    const text = vorgabeText.trim();
    if (!plan || !text || plan.vorgaben.includes(text)) return;
    aendern({ ...plan, vorgaben: [...plan.vorgaben, text] });
    setVorgabeText('');
  };

  const tagUmschalten = (tag: number) => {
    if (!plan) return;
    aendern({ ...plan, tage: plan.tage.map((t, i) => (i === tag ? { ...t, aktiv: !t.aktiv } : t)) });
  };

  const onDragStart = (e: DragStartEvent) => setZiehen(e.active.data.current as any);
  const onDragEnd = (e: DragEndEvent) => {
    setZiehen(null);
    const von = e.active.data.current as { tag: number; gang: string } | undefined;
    const nach = e.over?.data.current as { tag: number; gang: string } | undefined;
    if (!plan || !von || !nach || von.gang !== nach.gang || von.tag === nach.tag || !plan.tage[nach.tag].aktiv) return;
    if (tagGesperrt(plan.tage[von.tag]) || tagGesperrt(plan.tage[nach.tag])) return;
    // Tauschen (oder verschieben, wenn der Zieltag leer ist)
    const a = plan.tage[von.tag].slots[von.gang], b = plan.tage[nach.tag].slots[nach.gang];
    const tage = plan.tage.map((t, i) => {
      if (i !== von.tag && i !== nach.tag) return t;
      const slots = { ...t.slots };
      const neu = i === von.tag ? b : a;
      if (neu) slots[von.gang] = neu; else delete slots[von.gang];
      return { ...t, slots };
    });
    aendern({ ...plan, tage });
  };

  const zuordnungAendern = async (gang: string) => {
    if (!plan || !menue) return;
    const slot = plan.tage[menue.tag].slots[menue.gang];
    setMenue(null);
    if (!slot?.gericht_id) return;
    try {
      await api.patch(`/gerichte/${slot.gericht_id}/zuordnung`, { gang });
      setMeldung(`„${slot.name}“ ist jetzt ${ZUORDNUNGEN.find(z => z.gang === gang)?.label} – gilt für künftige Vorschläge.`);
    } catch {
      setFehler('Zuordnung konnte nicht gespeichert werden.');
    }
  };

  const wocheWechseln = (delta: number) => {
    if (!plan || navigationGesperrt) return;
    const d = new Date(plan.tage[0].datum + 'T12:00:00');
    d.setDate(d.getDate() + delta * 7);
    setWoche(kwVon(d));
  };

  if (laden && !plan) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  const sichtbareGaenge = plan?.gaenge || [];
  const menueSlot = menue && plan ? plan.tage[menue.tag].slots[menue.gang] : null;
  const menueZuordnung = menue ? menue.gang.replace(/^veg(Vorspeise|Suppe)$/, (_, x: string) => x.toLowerCase()) : '';

  return (
    <Box>
      {/* Kopf: Woche, Speicherstatus, Vorschlag */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Wochenplan KW {plan?.kw}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{plan && zeitraum(plan.tage)}</Typography>
          {plan?.versand && <Typography variant="caption" sx={{ color: 'success.main', display: 'block' }}>{versandText(plan.versand)}</Typography>}
        </Box>
        {!nurLesen && <Tooltip title={status === 'gespeichert' ? 'Alle Änderungen gespeichert' : status === 'speichert' ? 'Wird gespeichert …' : 'Speichern fehlgeschlagen'}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: status === 'fehler' ? 'error.main' : 'text.secondary', fontSize: '0.8rem', mr: 1 }}>
            {status === 'gespeichert' ? <CloudDone fontSize="small" /> : status === 'speichert' ? <CloudSync fontSize="small" /> : <CloudOff fontSize="small" />}
            {status === 'gespeichert' ? 'Gespeichert' : status === 'speichert' ? 'Speichert …' : 'Nicht gespeichert'}
          </Box>
        </Tooltip>}
        <IconButton disabled={navigationGesperrt || !plan} aria-label="Vorherige Woche" onClick={() => wocheWechseln(-1)}><ChevronLeft /></IconButton>
        <IconButton disabled={navigationGesperrt || !plan} aria-label="Nächste Woche" onClick={() => wocheWechseln(1)}><ChevronRight /></IconButton>
        {plan && (plan.jahr !== heute.jahr || plan.kw !== heute.kw) && (
          <Button disabled={navigationGesperrt} size="small" onClick={() => setWoche(heute)}>Diese Woche</Button>
        )}
        {!nurLesen && <>
          <Button variant="contained" startIcon={<AutoAwesome />} onClick={vorschlagStarten} disabled={!plan || navigationGesperrt}>
            Woche vorschlagen
          </Button>
          <Button variant="outlined" startIcon={<Send />} onClick={() => setVersandOffen(true)} disabled={!plan || navigationGesperrt}>
            Freigeben & senden
          </Button>
        </>}
      </Box>

      {/* Vorgaben */}
      {plan && (!nurLesen || plan.vorgaben.length > 0) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 2, p: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>Vorgaben:</Typography>
          {plan.vorgaben.map(v => (
            <Chip disabled={gesperrt} key={v} label={v} size="small" color={/^(kein|keine|keinen|ohne|nicht)\s/i.test(v) ? 'error' : 'success'} variant="outlined"
              onDelete={nurLesen ? undefined : () => aendern({ ...plan, vorgaben: plan.vorgaben.filter(x => x !== v) })} />
          ))}
          {!nurLesen && <TextField
            disabled={gesperrt} variant="standard" size="small" value={vorgabeText} placeholder="z. B. „Zander am Freitag“, „kein Lamm“ – Enter"
            onChange={e => setVorgabeText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); vorgabeHinzufuegen(); } }}
            InputProps={{ disableUnderline: true }} inputProps={{ 'aria-label': 'Neue Vorgabe', maxLength: 100 }}
            sx={{ flex: 1, minWidth: 220 }}
          />}
          {!nurLesen && vorgabeText.trim() && <Button disabled={gesperrt} size="small" onClick={vorgabeHinzufuegen}>Hinzufügen</Button>}
        </Box>
      )}

      {status === 'fehler' && plan && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => aendern(plan)}>Erneut speichern</Button>}>
        Änderungen sind noch nicht gespeichert. Bitte vor dem Verlassen erneut speichern.
      </Alert>}
      {fehler && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFehler('')} action={!plan ? <Button onClick={ladeWoche}>Erneut laden</Button> : undefined}>{fehler}</Alert>}
      {plan?.offeneVorgaben?.length ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPlan({ ...plan, offeneVorgaben: [] })}>
          {plan.offeneVorgaben.map(v => <div key={v}>{v}</div>)}
        </Alert>
      ) : null}
      <Box sx={{ height: 4, mb: 1 }}>{(vorschlagLaeuft || laden) && <LinearProgress />}</Box>

      {/* Wochenraster: Zeilen = Gänge, Spalten = Tage */}
      {plan && (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setZiehen(null)}>
          <Box component="fieldset" disabled={gesperrt} sx={{ border: 0, p: 0, m: 0, minWidth: 0, overflowX: 'auto', pb: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, minmax(150px, 1fr))', gap: 0.75, minWidth: 1172 }}>
              <Box />
              {plan.tage.map((t, i) => {
                const st = statusVon(t);
                const f = t.freigabe;
                const statusTitel = st === 'versendet' ? `Versendet am ${datumZeit(f!.versendet!.am)}${f?.versendet?.name ? ` von ${f.versendet.name}` : ''}`
                  : st === 'fertig' ? `Fertig${f?.fertig ? ` seit ${datumZeit(f.fertig.am)}${f.fertig.name ? ` (${f.fertig.name})` : ''}` : ''} – wird mit „Freigeben & senden“ verschickt`
                  : f?.geaendert ? 'Nach dem Versand wieder geöffnet – wird erneut gesendet' : 'In Arbeit';
                return (
                  <Box key={t.datum} sx={{ px: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 34 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: t.aktiv ? 'text.primary' : 'text.disabled' }}>
                        {TAG_KURZ[i]} <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>{datumKurz(t.datum)}</Typography>
                      </Typography>
                      {!nurLesen && !tagGesperrt(t) && <Tooltip title={t.aktiv ? 'Kein Pensionsmenü an diesem Tag' : 'Tag wieder planen'}>
                        <IconButton size="small" onClick={() => tagUmschalten(i)} aria-label={t.aktiv ? `${TAG_KURZ[i]}: kein Pensionsmenü` : `${TAG_KURZ[i]} wieder planen`}>
                          {t.aktiv ? <EventBusy sx={{ fontSize: 17, color: 'text.disabled' }} /> : <EventAvailable sx={{ fontSize: 17 }} color="primary" />}
                        </IconButton>
                      </Tooltip>}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minHeight: 28 }}>
                      {nurLesen ? (
                        t.nichtFreigegeben
                          ? <Chip size="small" variant="outlined" label="noch nicht freigegeben" sx={{ height: 20, fontSize: '0.68rem' }} />
                          : st === 'versendet' && <Tooltip title={statusTitel}><Chip size="small" color="success" variant="outlined" label="freigegeben" sx={{ height: 20, fontSize: '0.68rem' }} /></Tooltip>
                      ) : <>
                        <Tooltip title={statusTitel}>
                          <Chip size="small" variant={st === 'arbeit' ? 'outlined' : 'filled'} aria-label={`${TAG_KURZ[i]}: ${statusTitel}`}
                            color={st === 'versendet' ? 'success' : st === 'fertig' ? 'primary' : f?.geaendert ? 'warning' : 'default'}
                            label={st === 'versendet' ? 'versendet' : st === 'fertig' ? 'fertig' : f?.geaendert ? 'geändert' : 'in Arbeit'}
                            sx={{ height: 20, fontSize: '0.68rem' }} />
                        </Tooltip>
                        {st === 'arbeit' && <Button size="small" startIcon={<TaskAlt sx={{ fontSize: 15 }} />} disabled={navigationGesperrt}
                          onClick={() => tagFreigabe(i, 'fertig')} aria-label={`${TAG_KURZ[i]} als fertig markieren`} sx={{ minWidth: 0, py: 0, fontSize: '0.72rem' }}>Fertig</Button>}
                        {st === 'fertig' && <Button size="small" startIcon={<Undo sx={{ fontSize: 15 }} />} disabled={navigationGesperrt}
                          onClick={() => tagFreigabe(i, 'zuruecknehmen')} aria-label={`${TAG_KURZ[i]}: fertig zurücknehmen`} sx={{ minWidth: 0, py: 0, fontSize: '0.72rem' }}>Zurücknehmen</Button>}
                        {st === 'versendet' && t.datum >= heuteIso() && <Button size="small" startIcon={<LockOpen sx={{ fontSize: 15 }} />} disabled={navigationGesperrt}
                          onClick={() => tagFreigabe(i, 'aendern')} aria-label={`${TAG_KURZ[i]} ändern`} sx={{ minWidth: 0, py: 0, fontSize: '0.72rem' }}>Ändern</Button>}
                      </>}
                    </Box>
                  </Box>
                );
              })}

              {sichtbareGaenge.map(g => (
                <React.Fragment key={g.gang}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', pt: 1.25, lineHeight: 1.3 }}>
                    {g.label}
                  </Typography>
                  {plan.tage.map((t, i) => {
                    const slot = t.slots[g.gang];
                    const lesen = nurLesen || tagGesperrt(t);   // fertige und versendete Tage sind gesperrt
                    if (t.nichtFreigegeben) {
                      return (
                        <Box key={t.datum} sx={{ borderRadius: 1.5, minHeight: 40, bgcolor: '#f6f7f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {g === sichtbareGaenge[0] && <Typography variant="caption" sx={{ color: 'text.secondary' }}>noch nicht freigegeben</Typography>}
                        </Box>
                      );
                    }
                    if (!t.aktiv) {
                      return (
                        <Box key={t.datum} sx={{
                          borderRadius: 1.5, minHeight: 40,
                          background: 'repeating-linear-gradient(135deg, #eef0f3, #eef0f3 8px, #f6f7f9 8px, #f6f7f9 16px)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {g === sichtbareGaenge[0] && <Typography variant="caption" sx={{ color: 'text.secondary' }}>kein Pensionsmenü</Typography>}
                        </Box>
                      );
                    }
                    // Vegetarische Vorspeise/Suppe nur, wenn die reguläre nicht vegetarisch ist
                    if (g.nurWennNoetig && !slot && !t.vegNoetig?.[g.gang]) {
                      return (
                        <Box key={t.datum} sx={{ px: 1, py: 0.75, color: 'text.disabled', fontSize: '0.75rem' }}>
                          = {g.nurWennNoetig === 'vorspeise' ? 'Vorspeise' : 'Suppe'} (vegetarisch)
                        </Box>
                      );
                    }
                    const frei = g.gang === 'vestibuel';
                    return (
                      <Zelle key={t.datum} tag={i} gang={g.gang} aktivGang={ziehen ? ziehen.gang : null} gesperrt={lesen}>
                        {slot && lesen ? (
                          <GerichtKarte tag={i} gang={g.gang} slot={slot} hinweise={[]} ziehbar={false} />
                        ) : slot ? (
                          <GerichtKarte
                            tag={i} gang={g.gang} slot={slot} hinweise={plan.hinweise[`${i}:${g.gang}`] || []} ziehbar={!frei && !gesperrt}
                            onFixieren={frei ? undefined : () => slotSetzen(i, g.gang, { ...slot, fixiert: !slot.fixiert })}
                            onAlternativen={frei ? undefined : () => setAuswahl({ tag: i, gang: g.gang })}
                            onBearbeiten={() => setBearbeiten({ tag: i, gang: g.gang, text: slot.name })}
                            onMenue={e => setMenue({ anker: e.currentTarget, tag: i, gang: g.gang })}
                          />
                        ) : lesen ? (
                          <Box sx={{ minHeight: frei ? 40 : 84, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }} />
                        ) : (
                          <Button fullWidth size="small" startIcon={<Add />}
                            onClick={() => (frei ? setBearbeiten({ tag: i, gang: g.gang, text: '' }) : setAuswahl({ tag: i, gang: g.gang }))}
                            sx={{ minHeight: frei ? 40 : 84, border: '1px dashed', borderColor: 'divider', color: 'text.secondary', fontWeight: 500 }}>
                            {frei ? 'Eintragen' : 'Gericht wählen'}
                          </Button>
                        )}
                      </Zelle>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          </Box>
          <DragOverlay dropAnimation={null}>
            {ziehen && plan.tage[ziehen.tag].slots[ziehen.gang] && (
              <Box sx={{ width: 170 }}>
                <GerichtKarte tag={ziehen.tag} gang={ziehen.gang} slot={plan.tage[ziehen.tag].slots[ziehen.gang]} hinweise={[]} ziehbar={false} overlay />
              </Box>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {plan && !nurLesen && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
          Ziehen am Griff ⠿ tauscht ein Gericht mit einem anderen Tag (nur im selben Gang). 📌 Fixierte Gerichte bleiben bei „Woche vorschlagen“ erhalten.
          „Fertig“ sperrt einen Tag; „Freigeben & senden“ verschickt nur fertige Tage. Hinweise sind nur Empfehlungen – entschieden wird in der Küche.
        </Typography>
      )}

      {/* Aktionsmenü einer Karte */}
      <Menu anchorEl={menue?.anker} open={!!menue} onClose={() => setMenue(null)}>
        {menueSlot?.gericht_id && <ListSubheader sx={{ lineHeight: '32px' }}>Gericht gehört zu …</ListSubheader>}
        {menueSlot?.gericht_id && ZUORDNUNGEN.map(z => (
          <MenuItem key={z.gang} dense onClick={() => zuordnungAendern(z.gang)}>
            <ListItemIcon>{z.gang === menueZuordnung && <Check fontSize="small" />}</ListItemIcon>
            <ListItemText>{z.label}</ListItemText>
          </MenuItem>
        ))}
        {menueSlot?.gericht_id && <Divider />}
        <MenuItem dense onClick={() => { if (menue) slotSetzen(menue.tag, menue.gang, null); setMenue(null); }}>
          <ListItemIcon><DeleteOutline fontSize="small" /></ListItemIcon>
          <ListItemText>Aus dem Plan entfernen</ListItemText>
        </MenuItem>
      </Menu>

      {plan && auswahl && (
        <AuswahlDialog
          plan={plan} tag={auswahl.tag} gang={auswahl.gang}
          label={plan.gaenge.find(g => g.gang === auswahl.gang)?.label || ''}
          onClose={() => setAuswahl(null)}
          onWaehlen={g => {
            const alt = plan.tage[auswahl.tag].slots[auswahl.gang];
            slotSetzen(auswahl.tag, auswahl.gang, { gericht_id: g.id, name: g.name, fixiert: alt?.fixiert || false, angepasst: false });
            setAuswahl(null);
          }}
          nurDaten={nurDaten}
        />
      )}

      {/* Für diesen Tag anpassen */}
      <Dialog open={!!bearbeiten} onClose={() => setBearbeiten(null)} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (!bearbeiten || !plan || !bearbeiten.text.trim()) return;
          const alt = plan.tage[bearbeiten.tag].slots[bearbeiten.gang];
          const text = bearbeiten.text.trim();
          slotSetzen(bearbeiten.tag, bearbeiten.gang, {
            gericht_id: alt?.gericht_id ?? null, name: text, fixiert: alt?.fixiert || false, vorgabe: alt?.vorgabe,
            angepasst: bearbeiten.gang === 'vestibuel' ? false : (alt?.angepasst || (!!alt && alt.name !== text)),
          });
          setBearbeiten(null);
        }}>
          <DialogTitle>
            {bearbeiten && plan && `${plan.gaenge.find(g => g.gang === bearbeiten.gang)?.label} · ${TAG_KURZ[bearbeiten.tag]} ${datumKurz(plan.tage[bearbeiten.tag].datum)}`}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              Die Änderung gilt nur für diesen Tag – das Gericht in der Datenbank bleibt unverändert.
            </Typography>
            <TextField
              fullWidth autoFocus multiline minRows={2} label="Text auf der Karte" value={bearbeiten?.text || ''}
              onChange={e => setBearbeiten(b => (b ? { ...b, text: e.target.value } : b))}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); (e.target as HTMLElement).closest('form')?.requestSubmit(); } }}
              inputProps={{ maxLength: 300 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBearbeiten(null)}>Abbrechen</Button>
            <Button type="submit" variant="contained" disabled={!bearbeiten?.text.trim()}>Übernehmen</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Vorschlag ersetzt nicht fixierte Gerichte */}
      <Dialog open={vorschlagFragen} onClose={() => setVorschlagFragen(false)}>
        <DialogTitle>Woche neu vorschlagen?</DialogTitle>
        <DialogContent>
          <Typography>Alle Gerichte, die nicht mit 📌 fixiert sind, werden durch neue Vorschläge ersetzt. Vorgaben werden berücksichtigt.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVorschlagFragen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={wocheVorschlagen}>Vorschlagen</Button>
        </DialogActions>
      </Dialog>

      {versandOffen && plan && (
        <VersandDialog jahr={plan.jahr} kw={plan.kw} versand={plan.versand} verteilerAendern={istAdmin} onClose={() => setVersandOffen(false)}
          tage={plan.tage.flatMap((t, i) => (statusVon(t) === 'fertig' ? [`${TAG_KURZ[i]} ${datumKurz(t.datum)}${t.freigabe?.geaendert ? ' (geändert)' : ''}`] : []))}
          onGesendet={(v, antwort) => {
            setVersandOffen(false);
            setPlan(p => (p && p.jahr === plan.jahr && p.kw === plan.kw ? { ...p, ...(antwort?.tage ? antwort : {}), versand: v } : p));
            setMeldung(`${v.tage?.length === 1 ? '1 Tag' : `${v.tage?.length ?? ''} Tage`} an ${v.empfaenger} Empfänger gesendet`);
          }} />
      )}

      <Snackbar open={!!meldung} autoHideDuration={3500} onClose={() => setMeldung('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setMeldung('')}>{meldung}</Alert>
      </Snackbar>
    </Box>
  );
}

// --- Alternativen + Suche für einen Platz ---
function AuswahlDialog({ plan, tag, gang, label, onClose, onWaehlen, nurDaten }: {
  plan: Plan; tag: number; gang: string; label: string;
  onClose: () => void; onWaehlen: (g: { id: number; name: string }) => void;
  nurDaten: (p: Plan) => object;
}) {
  const [alternativen, setAlternativen] = useState<Alternative[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<{ id: number; name: string }[] | null>(null);
  const [suchFehler, setSuchFehler] = useState('');
  const [sucht, setSucht] = useState(false);

  useEffect(() => {
    api.post(`/wochenplan/woche/${plan.jahr}/${plan.kw}/alternativen`, { tag, gang, plan: nurDaten(plan) })
      .then(res => setAlternativen(res.data))
      .catch(() => setFehler('Alternativen konnten nicht geladen werden.'));
    // nur beim Öffnen laden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const s = suche.trim();
    const controller = new AbortController();
    setSuchFehler('');
    setTreffer(null);
    if (s.length < 2) { setSucht(false); return; }
    setSucht(true);
    const timer = setTimeout(() => {
      api.get('/gerichte', { params: { suche: s, kategorie: KATEGORIE_FUER[gang], limit: '20' }, signal: controller.signal })
        .then(res => { if (!controller.signal.aborted) setTreffer(res.data.gerichte); })
        .catch(() => { if (!controller.signal.aborted) setSuchFehler('Suche fehlgeschlagen. Bitte erneut versuchen.'); })
        .finally(() => { if (!controller.signal.aborted) setSucht(false); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [suche, gang]);

  const aktuell = plan.tage[tag].slots[gang];
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{label} · {TAG_KURZ[tag]} {datumKurz(plan.tage[tag].datum)}</DialogTitle>
      <DialogContent>
        {aktuell && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>Aktuell: {aktuell.name}</Typography>}
        <TextField
          fullWidth size="small" autoFocus placeholder="Gericht suchen …" value={suche} onChange={e => setSuche(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          inputProps={{ 'aria-label': 'Gericht suchen' }}
          sx={{ mb: 2 }}
        />
        {sucht ? <CircularProgress size={28} /> : suchFehler ? <Alert severity="error">{suchFehler}</Alert> : treffer ? (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>Suchergebnis</Typography>
            {treffer.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nichts gefunden.</Typography>}
            {treffer.map(g => (
              <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{g.name}</Typography>
                <Button size="small" variant="outlined" onClick={() => onWaehlen(g)}>Übernehmen</Button>
              </Box>
            ))}
          </>
        ) : (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>Vorschläge</Typography>
            {fehler && <Alert severity="error">{fehler}</Alert>}
            {!alternativen && !fehler && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
            {alternativen?.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Keine passenden Alternativen gefunden.</Typography>}
            {alternativen?.map(a => (
              <Box key={a.gericht.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.gericht.name}</Typography>
                  {a.gruende.length > 0 && <Typography variant="caption" sx={{ color: 'success.dark', display: 'block' }}>✓ {a.gruende.join(' · ')}</Typography>}
                  {a.hinweise.length > 0 && <Typography variant="caption" sx={{ color: '#7a4f00', display: 'block' }}>Hinweis: {a.hinweise.join(' · ')}</Typography>}
                </Box>
                <Button size="small" variant="outlined" onClick={() => onWaehlen(a.gericht)}>Übernehmen</Button>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Schließen</Button></DialogActions>
    </Dialog>
  );
}
